<template>
    <v-select
        v-model="locale"
        :items="locales"
        :menu-props="{
            contentClass: 'locale-menu-content'
        }"
        menu-icon="chevronDown"
        :class="['v-select-locale', { 'v-select-locale--dark': dark }]"
        item-title="name"
        item-value="id"
        variant="plain"
        density="compact"
        hide-details
        text
    >
        <template #selection="{ item }">
            <span :class="['locale-select__value body-medium text-uppercase', dark ? 'text-white' : 'text-dark']">
                {{ item.value }}
            </span>
        </template>
    </v-select>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { useLocale } from 'vuetify';

import { useLocaleStore } from '@/store/locales';

const props = defineProps({
    reloadPage: {
        type: Boolean,
        default: false
    },
    dark: {
        type: Boolean,
        default: false
    }
});

const { locale, locales } = useLocaleSelect();

/**
 * Reusable functions specific to this component
 */
function useLocaleSelect() {
    // Global
    const { current } = useLocale();
    const localeStore = useLocaleStore();
    const { locales } = storeToRefs(localeStore);

    // Local
    const locale = ref('');

    locale.value = localeStore.getLocale;
    current.value = locale.value;

    watch(locale, () => {
        localeStore.setLocale(locale.value);

        if (props.reloadPage) {
            localeStore.clearLocales();
            window.location.reload();
        } else {
            current.value = locale.value;
        }
    });

    return {
        locale,
        locales
    };
}
</script>

<style lang="scss" scoped>
.v-select-locale {
    :deep(.v-field) {
        padding: 8px 12px;
        align-items: center;
    }
    :deep(.v-field__field) {
        align-items: center;
    }
    :deep(.v-field__input) {
        padding: 0;
        text-transform: uppercase;
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
    }
    /* Wrapper around #selection; Vuetify uses inline-flex + line-height: inherit (body-medium ~22px), which misaligns vs the icon row. */
    :deep(.v-select__selection) {
        display: inline-flex;
        align-items: center;
        line-height: 1;
        margin-top: 4px;
    }
    :deep(.locale-select__value) {
        line-height: 1 !important;
    }
    :deep(.v-field__append-inner) {
        padding: 0 !important;
        display: flex;
        align-items: center;
        align-self: center;
    }
    /* Default append icon is ~1.5em (tall box); cap height matches label better at 1em. Slight SVG nudge for optical center vs capitals. */
    :deep(.v-field__append-inner > .v-icon) {
        font-size: 1em !important;
    }
    :deep(.v-field__append-inner .v-icon__svg) {
        transform: translateY(-0.07em);
    }
    :deep(.v-field__append-inner svg) {
        transition: none !important;
    }

    &--dark {
        :deep(.v-field__input) {
            color: white;
        }
        :deep(.v-field__append-inner svg) {
            color: white;
        }
    }
}
</style>
